import { useMemo } from 'react';
import { useListClaimsQuery } from '../data';
import type { ClaimQueueItemView, ClaimStatus } from '../../../data/views';

/** Discriminated view-state for the per-center claims queue (Constitution §84). */
export type ClaimsQueueState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'empty' }
  | { status: 'data'; items: ClaimQueueItemView[]; isRefreshing: boolean };

/**
 * Loads the claims queue for a single center (no "All Centers" scope, spec decision #12).
 * The query is skipped until a `center` is chosen, in which case the state is `empty`.
 */
export const useClaimsQueue = (center: string, status?: ClaimStatus): ClaimsQueueState => {
  const { data, isLoading, isFetching, isError } = useListClaimsQuery(
    { center, status },
    { skip: !center },
  );

  return useMemo<ClaimsQueueState>(() => {
    if (!center)
      return { status: 'empty' };

    if (isLoading)
      return { status: 'loading' };

    if (isError)
      return { status: 'error' };

    const items = data ?? [];

    return items.length === 0
      ? { status: 'empty' }
      : { status: 'data', items, isRefreshing: isFetching };
  }, [center, data, isLoading, isFetching, isError]);
};
